import React from "react";
import { getCountryOptions, flagFromCode, dialCodeFor } from "@/lib/countries";
import { useI18n } from "@/context/I18nContext";

/**
 * Country dropdown — emits ISO2 code via onChange.
 */
export function CountrySelect({ value, onChange, testid = "country-select", required = false, placeholder }) {
  const { lang } = useI18n();
  const opts = React.useMemo(() => getCountryOptions(lang), [lang]);
  return (
    <select
      data-testid={testid}
      required={required}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
    >
      <option value="">{placeholder || (lang === "el" ? "— Επιλέξτε χώρα —" : "— Select country —")}</option>
      {opts.map((o) => (
        <option key={o.code} value={o.code}>
          {flagFromCode(o.code)} {o.label} ({o.dial})
        </option>
      ))}
    </select>
  );
}

/**
 * Phone input with auto-prefix based on country code.
 * - `value` is the full E.164-ish string (e.g. "+30 210 1234567")
 * - When `countryCode` changes, if the phone is empty/just prefix, sets the new prefix
 */
export function PhoneWithCountry({ value, onChange, countryCode, testid = "phone-input", placeholder, required }) {
  const dial = dialCodeFor(countryCode);

  // Auto-prefix when country changes & phone is empty/has only old prefix
  React.useEffect(() => {
    if (!dial) return;
    const v = (value || "").trim();
    if (!v) {
      onChange(`${dial} `);
      return;
    }
    // Replace previous dial code (anything that starts with +<digits><space>) only if it differs and the rest is empty
    const m = v.match(/^\+\d{1,4}\s*(.*)$/);
    if (m) {
      const rest = m[1].trim();
      if (!rest) {
        onChange(`${dial} `);
      } else if (!v.startsWith(dial)) {
        onChange(`${dial} ${rest}`);
      }
    } else {
      // doesn't start with +, prepend
      onChange(`${dial} ${v}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  return (
    <div className="mt-1.5 flex items-center gap-2">
      <div className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-slate-50 text-slate-600 min-w-[78px] font-mono">
        {dial || "+—"}
      </div>
      <input
        data-testid={testid}
        type="tel"
        required={required}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        className="flex-1 px-3 py-2 border border-slate-200 rounded-md text-sm"
      />
    </div>
  );
}
